import React from 'react';
import * as Yup from 'yup';
import { Field, FieldArray, Form, Formik } from 'formik';
import { Checkbox, DropdownField, TextAreaField, TextField } from './elements';
import {
  facetWidgetOptions,
  openSearchFacetDefaults,
  openSearchProviderDetails,
  openSearchSortingDefaults,
  requiredMappingKeys,
  VIEW_MODE_FIELDS,
  viewModeOptions
} from './default-values';
import classnames from 'classnames';

const validationSchema = Yup.object().shape({
  title: Yup.string().required(`Can't be blank`),
  // Each provider validates only its active view mode's required mapping fields, so unused
  // modes can stay empty. The error paths line up with the rendered field names.
  openSearchProviders: Yup.array().of(
    Yup.lazy((provider) => {
      const required = requiredMappingKeys(provider?.viewMode);
      if (required.length === 0) return Yup.object();

      return Yup.object({
        viewModeConfigs: Yup.object({
          [provider.viewMode]: Yup.object({
            mapping: Yup.object(
              Object.fromEntries(
                required.map(key => [key, Yup.string().trim().required('Required')])
              )
            ),
          }),
        }),
      });
    })
  ),
})

function ViewModeField({field, namePrefix}) {
  const name = `${namePrefix}.${field.name}`;
  const label = field.required ? `${field.label} *` : field.label;

  if (field.kind === 'checkbox') {
    return <Checkbox name={name} label={label}/>;
  }

  if (field.kind === 'textarea') {
    return (
      <>
        <TextAreaField name={name} label={label} rows={field.rows}/>
        {field.hint && <small className='form-hint'>{field.hint}</small>}
      </>
    );
  }

  return <TextField name={name} label={label} placeholder=''/>;
}

// View mode selector + mode-specific fields for a single provider. Every mode's config is
// kept simultaneously under the provider's `viewModeConfigs` object, so the dropdown just
// changes which subtree is rendered — switching back and forth preserves each mode's edits,
// and the active `viewMode` decides which config the public page renders.
function ProviderViewMode({idx, viewMode}) {
  const base = `openSearchProviders[${idx}]`;
  const modePrefix = `${base}.viewModeConfigs.${viewMode}`;

  return (
    <div>
      <div className='form-section'>
        <div className='row'>
          <div className='col-12'>
            <h3 className='h--smallest text-white'>View Mode</h3>
          </div>
          <div className='col-12'>
            <DropdownField
              name={`${base}.viewMode`}
              label={null}
              options={viewModeOptions}/>
            <small className='form-hint'>
              Sets how each result renders. Map each field below to an index attribute; leave
              blank to hide it. Each mode keeps its own mapping.
            </small>
          </div>
        </div>
      </div>
      <div className='form-section'>
        <div className='row'>
          {(VIEW_MODE_FIELDS[viewMode] || []).map((field) => (
            <div
              key={field.name}
              className={field.className ? field.className : field.kind === 'checkbox' ? 'col-3' : 'col-12'}>
              <ViewModeField field={field} namePrefix={modePrefix}/>
            </div>
          ))}
        </div>
      </div>
    </div>

  );
}

export default function OpenSearchSettingsForm({submitHandler, defaultValues}) {
  return (
    <div className='open-search-settings'>
      <Formik
        initialValues={defaultValues}
        validationSchema={validationSchema}
        onSubmit={values => submitHandler(values)}>
        {({values}) => {
          let providesCount = 0;
          if (values.openSearchProviders.length > 0) {
            providesCount += values.openSearchProviders.length;
          }

          const css = {
            root: classnames(
              {
                'open-search-setting-layout': true,
                [`open-search-setting-layout--${values.displayOptions.layout}`]: true
              }
            )
          }

          return (
            <Form>
              <div className={css.root}>
                <div className='nv-open-search-settings-form__field'>
                  <div className='form-section'>
                    <div className='row'>
                      <div className='col-12'>
                        <TextField name='title' label='Title'/>
                      </div>
                      <div className='col-6'>
                        <DropdownField
                          name='displayOptions.layout'
                          label='Layout'
                          options={[
                            ['standard', 'Standard'],
                            ['compact', 'Compact'],
                          ]}
                        />
                      </div>
                      <div className='col-6'>
                        <TextField
                          name={`displayOptions.itemsPerPage`}
                          label='Items per page'
                          type={'number'}/>
                      </div>
                      <div className='col-3'>
                        <Checkbox
                          name={`displayOptions.showFacetsCount`}
                          label='Show facets count'/>
                      </div>
                      <div className='col-3'>
                        <Checkbox
                          name={`displayOptions.stickyNavigation`}
                          label='Sticky navigation'/>
                      </div>
                      <div className='col-3'>
                        <Checkbox
                          name={`displayOptions.hideCategoryLabel`}
                          label='Hide category label'/>
                      </div>
                    </div>
                  </div>

                  <div className='form-section compact-mode-visible'>
                    <div className='row'>
                      <div className='col-12'>
                        <h3 className='h--smallest text-white mt-0'>Compact mode settings</h3>
                      </div>
                      <div className='col-12'>
                        <TextField
                          name={`compactMode.dropdownLabel`}
                          label='Dropdown label'
                          placeholder='Learning levels'/>
                        <p className='p--small text-white'>'Filter by' dropdown label</p>
                      </div>
                      <div className='col-4'>
                        <TextField
                          name={`compactMode.cta.text`}
                          label='CTA Text'
                          placeholder='View All'/>
                      </div>
                      <div className='col-8'>
                        <TextField
                          name={`compactMode.cta.url`}
                          label='CTA URL'
                          placeholder='https://developer.nvidia.com/learning-library'/>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <FieldArray name='openSearchProviders'>
                    {({remove, push, form}) => {
                      return (
                        <div className='providers-section open-search-providers'>
                          {values.openSearchProviders && values.openSearchProviders.length > 0 &&
                            values.openSearchProviders.map((entry, idx) => {
                              return (
                                <div className='group-settings' key={idx}>
                                  <details open={true}>
                                    <summary>{entry.id || 'New provider'}</summary>
                                    <div>
                                      <div className='form-section'>
                                        <div className='row'>
                                          <div className='col-12'>
                                            <h3 className='h--smallest text-white mt-3'>Basic index settings</h3>
                                            <Field
                                              name={`openSearchProviders[${idx}].provider`}
                                              type='hidden'/>
                                          </div>
                                          <div className='col-12'>
                                            <TextField
                                              name={`openSearchProviders[${idx}].id`}
                                              label='Provider ID'
                                              placeholder='e.g. my-provider'/>
                                            <small className='form-hint'>Used internally to identify this provider. Not displayed to end users.</small>
                                          </div>
                                          <div className='col-12'>
                                            <TextAreaField
                                              name={`openSearchProviders[${idx}].indexes`}
                                              label='Indexes'
                                              placeholder='index-name-1, index-name-2'
                                              rows={2}/>
                                            <small className='form-hint'>Comma-separated list of OpenSearch index names to query.</small>
                                          </div>
                                          <div className='col-12'>
                                            <TextField
                                              name={`openSearchProviders[${idx}].aggregation`}
                                              label='Group by'
                                              placeholder='e.g. document_id'/>
                                            <small className='form-hint'>Deduplicates results when querying multiple indexes. Supports a single field name only.</small>
                                          </div>
                                          <div className='col-12'>
                                            <TextAreaField
                                              name={`openSearchProviders[${idx}].filters`}
                                              label='Filters'
                                              rows={6}/>
                                            <details>
                                              <summary>
                                                Show example
                                              </summary>
                                              <pre className='my-2' style={{fontSize: '12px'}}>
{`[
  {
    "field": "learning_level",
    "values": ["Intermediate"]
  },
  {
    "field": "technologies",
    "values": ["BioNeMo", "Blackwell"]
  },
  {
    "field": "products",
    "values": ["DRIVE AGX Orin", "DRIVE AGX Thor", "DRIVE AGX Xavier", "DRIVE PX 2"],
    "type": "any"
  },
  {
    "field": "status",
    "values": ["draft"],
    "type": "none"
  }
]`}
                                              </pre>
                                            </details>
                                            <small className='form-hint'>
                                              JSON array of global filters applied to every query.
                                              See <a target={'_blank'} rel='noopener noreferrer' href="https://www.elastic.co/docs/reference/search-ui/api-core-configuration#api-core-configuration-filters-global-filters">
                                              Search UI filter configuration</a> for supported options.
                                            </small>
                                          </div>
                                        </div>
                                      </div>

                                      <div className='form-section'>
                                        <div className='row'>
                                          <FieldArray name={`openSearchProviders[${idx}].sorting`}>
                                            {({remove: removeSorting, push: pushSorting, form}) => {
                                              const items = form.values.openSearchProviders[idx].sorting;
                                              return (
                                                <div>
                                                  <h3 className='h--smallest text-white'>Sorting</h3>
                                                  {items && items.length > 0 && items.map((item, sortingIdx) => {
                                                    return (
                                                      <div key={item.id} className='row'>
                                                        <div className='col-4'>
                                                          <TextField
                                                            name={`openSearchProviders[${idx}].sorting[${sortingIdx}].label`}
                                                            label='Label'
                                                            placeholder='A-Z (ascending)'/>
                                                        </div>
                                                        <div className='col-4'>
                                                          <TextField
                                                            name={`openSearchProviders[${idx}].sorting[${sortingIdx}].field`}
                                                            label='Field'
                                                            placeholder='document_title'/>
                                                        </div>
                                                        <div className='col-3'>
                                                          <DropdownField
                                                            name={`openSearchProviders[${idx}].sorting[${sortingIdx}].direction`}
                                                            label='Direction'
                                                            options={[
                                                              ['asc', 'Ascending'],
                                                              ['desc', 'Descending'],
                                                            ]}
                                                          />
                                                        </div>
                                                        <div className='col-1 align-self-end my-2'>
                                                          <button
                                                            type='button'
                                                            className='btn btn-danger btn-sm'
                                                            onClick={() => removeSorting(sortingIdx)}>×
                                                          </button>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                  <div className='row'>
                                                    <div className='col-12 mt-3'>
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-primary btn-sm'
                                                        onClick={() => pushSorting(
                                                          {...openSearchSortingDefaults, id: crypto.randomUUID()}
                                                        )}>+ Add sorting option
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            }}
                                          </FieldArray>
                                        </div>
                                      </div>

                                      <div className='form-section'>
                                        <div className='row'>
                                          <FieldArray name={`openSearchProviders[${idx}].facets`}>
                                            {({remove: removeFacet, push: pushFacet, form}) => {
                                              const items = form.values.openSearchProviders[idx].facets;
                                              return (
                                                <div>
                                                  <h3 className='h--smallest text-white'>Facets</h3>
                                                  {items && items.length > 0 && items.map((item, facetidx) => {
                                                    return (
                                                      <div key={item.id} className='row'>
                                                        <div className='col-3'>
                                                          <TextField
                                                            name={`openSearchProviders[${idx}].facets[${facetidx}].field`}
                                                            label='Attribute'
                                                            placeholder='Field name'/>
                                                        </div>
                                                        <div className='col-3'>
                                                          <TextField
                                                            name={`openSearchProviders[${idx}].facets[${facetidx}].label`}
                                                            label='Label'
                                                            placeholder='Facet label'/>
                                                        </div>
                                                        <div className='col-3'>
                                                          <DropdownField
                                                            name={`openSearchProviders[${idx}].facets[${facetidx}].widget`}
                                                            label='Widget'
                                                            options={facetWidgetOptions}/>
                                                        </div>
                                                        <div className='col-2'>
                                                          <div className='d-flex h-100 align-items-end'>
                                                            <Checkbox
                                                              name={`openSearchProviders[${idx}].facets[${facetidx}].hidden`}
                                                              label='Hidden'
                                                            />
                                                          </div>
                                                        </div>
                                                        <div className='col-1 align-self-end my-2'>
                                                          <button
                                                            type='button'
                                                            className='btn btn-danger btn-sm'
                                                            onClick={() => removeFacet(facetidx)}>×
                                                          </button>
                                                        </div>
                                                      </div>
                                                    );
                                                  })}
                                                  <div className='row'>
                                                    <div className='col-12 mt-3'>
                                                      <button
                                                        type='button'
                                                        className='btn btn-outline-primary btn-sm'
                                                        onClick={() => pushFacet(
                                                          {...openSearchFacetDefaults, id: crypto.randomUUID()}
                                                        )}>
                                                        Add filter
                                                      </button>
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            }}
                                          </FieldArray>
                                        </div>
                                      </div>

                                      <ProviderViewMode idx={idx} viewMode={entry.viewMode}/>

                                      <div className='row'>
                                        <div className='col-12 my-2 pb-4'>
                                          <button
                                            type='button'
                                            className='btn btn-outline-warning btn-sm'
                                            onClick={() => remove(idx)}>
                                            Remove provider
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </details>
                                </div>
                              );
                            })}
                          {providesCount === 0 && (
                            <button
                              type='button'
                              className='btn btn-outline-light btn-sm'
                              onClick={() => push({...openSearchProviderDetails})}>
                              Add OpenSearch provider
                            </button>
                          )}
                        </div>
                      );
                    }}
                  </FieldArray>
                </div>
              </div>

              <div className='form-actions'>
                <button type='submit' className='btn btn-info btn-sm'>Save settings</button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}
